// The signup page's three-step strip and its Delivery / Pickup cards (design
// "4F4"): the intake-questions-first experiment's "account-first-redesign"
// arm, shipped to every funnel where pickup is available; see
// docs/intake-questions-first-experiment.md.
//
// FulfillmentOptions only informs: the cards are not controls and nothing on
// them is clickable. The visitor chooses delivery or pickup later, at
// select-pharmacy.

import { makeStyles } from "@mui/styles";

import { kickAmaOrange, kickBlue, kickNavy } from "~/shared/styles/baseStyles";

const DELIVERY_FILL = "#e6e5f5";
const PICKUP_FILL = "#fff3df";
// Darker than the brand orange so a 1px pickup border still reads on cream.
const PICKUP_ACCENT = "#c77d0a";
const PICKUP_ICON = "#8a5200";
const FREE_GREEN = "#007a52";
const MUTED_TEXT = "#4a4d70";
const DESKTOP = "@media (min-width: 600px)";
// The brand bold face is a separate family with no fallback of its own; a
// sans fallback keeps these labels sans-serif when the CDN font has not
// loaded, and no synthesis keeps the real face from being bolded twice.
const BOLD_FONT = {
  fontFamily:
    '"BasisGrotesquePro-Bold", "Helvetica Neue", Helvetica, Arial, sans-serif',
  fontWeight: 700,
  fontSynthesis: "none",
};

const useStyles = makeStyles({
  steps: {
    margin: 0,
    padding: 0,
    listStyle: "none",
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: "6px",
    textAlign: "center",
    fontSize: "12px",
    lineHeight: 1.3,
    color: kickNavy,
    [DESKTOP]: { gap: "8px", fontSize: "13px" },
  },
  step: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "6px",
  },
  stepNumber: {
    width: "26px",
    height: "26px",
    borderRadius: "50%",
    backgroundColor: kickBlue,
    color: "#ffffff",
    ...BOLD_FONT,
    fontSize: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    [DESKTOP]: { width: "28px", height: "28px", fontSize: "13px" },
  },
  stepNumberLast: {
    backgroundColor: kickAmaOrange,
    color: kickNavy,
  },
  stepLabelLast: {
    ...BOLD_FONT,
  },
  options: {
    margin: 0,
    padding: 0,
    listStyle: "none",
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "8px",
    [DESKTOP]: { gap: "10px" },
  },
  // Information, not a control: no pointer, hover, focus or selected state.
  card: {
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "6px",
    margin: 0,
    padding: "12px",
    borderRadius: "12px",
    borderWidth: "1px",
    borderStyle: "solid",
    color: kickNavy,
    [DESKTOP]: { gap: "8px", padding: "14px" },
  },
  delivery: {
    backgroundColor: DELIVERY_FILL,
    borderColor: kickBlue,
  },
  pickup: {
    backgroundColor: PICKUP_FILL,
    borderColor: PICKUP_ACCENT,
  },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    height: "22px",
    padding: "0 8px",
    borderRadius: "11px",
    ...BOLD_FONT,
    fontSize: "11px",
    letterSpacing: "0.06em",
    [DESKTOP]: {
      height: "24px",
      padding: "0 10px",
      borderRadius: "12px",
      fontSize: "12px",
    },
  },
  badgeFree: { backgroundColor: FREE_GREEN, color: "#ffffff" },
  badgeSameDay: { backgroundColor: kickAmaOrange, color: kickNavy },
  title: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    ...BOLD_FONT,
    fontSize: "16px",
    lineHeight: 1.15,
    [DESKTOP]: { fontSize: "18px" },
  },
  icon: {
    flex: "none",
    width: "26px",
    height: "26px",
    [DESKTOP]: { width: "28px", height: "28px" },
  },
  detail: {
    fontSize: "12px",
    lineHeight: 1.3,
    color: MUTED_TEXT,
    [DESKTOP]: { fontSize: "13px" },
  },
});

// SignupSteps is the "1 intake, 2 doctor, 3 delivered or picked up" strip.
export function SignupSteps() {
  const classes = useStyles();
  return (
    <ol className={classes.steps}>
      <li className={classes.step}>
        <span className={classes.stepNumber} aria-hidden="true">
          1
        </span>
        <span>Complete online intake</span>
      </li>
      <li className={classes.step}>
        <span className={classes.stepNumber} aria-hidden="true">
          2
        </span>
        <span>A US doctor reviews your visit</span>
      </li>
      <li className={classes.step}>
        <span
          className={`${classes.stepNumber} ${classes.stepNumberLast}`}
          aria-hidden="true"
        >
          3
        </span>
        <span className={classes.stepLabelLast}>Delivered, or picked up</span>
      </li>
    </ol>
  );
}

const TruckIcon = ({ className }) => (
  <svg
    aria-hidden="true"
    focusable="false"
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke={kickBlue}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 7h11v9H3zM14 10h4l3 3v3h-7z" />
    <circle cx="7" cy="18" r="1.6" />
    <circle cx="17" cy="18" r="1.6" />
  </svg>
);

const StoreIcon = ({ className }) => (
  <svg
    aria-hidden="true"
    focusable="false"
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke={PICKUP_ICON}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 10l1.5-5h13L20 10M4 10v10h16V10M4 10h16M9 20v-6h6v6" />
  </svg>
);

const CARDS = [
  {
    key: "delivery",
    badge: "FREE",
    Icon: TruckIcon,
    title: "Delivery",
    detail: "3 to 9 days · FedEx 2-day available",
  },
  {
    key: "pickup",
    badge: "SAME DAY",
    Icon: StoreIcon,
    title: "Pickup",
    detail: "At a local pharmacy you choose",
  },
];

// FulfillmentOptions: the Delivery and Pickup cards, as a list of two.
export function FulfillmentOptions() {
  const classes = useStyles();
  return (
    <ul className={classes.options} aria-label="Delivery or pickup">
      {CARDS.map(({ key, badge, Icon, title, detail }) => {
        const isDelivery = key === "delivery";
        return (
          <li
            key={key}
            className={`${classes.card} ${
              isDelivery ? classes.delivery : classes.pickup
            }`}
            data-testid={`fulfillment-${key}`}
          >
            <span
              className={`${classes.badge} ${
                isDelivery ? classes.badgeFree : classes.badgeSameDay
              }`}
            >
              {badge}
            </span>
            <span className={classes.title}>
              <Icon className={classes.icon} />
              {title}
            </span>
            <span className={classes.detail}>{detail}</span>
          </li>
        );
      })}
    </ul>
  );
}
